import { Injectable, computed, inject, signal } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { SupabaseService } from '../supabase/supabase.service';
import type { Account, AccountMember } from './account.model';

/** Última cuenta visitada. La usan el redirect posterior al login y el selector. */
export const LAST_ACCOUNT_KEY = 'liciya.last_account_slug';

/**
 * La cuenta activa, resuelta desde el `slug` de la URL.
 *
 * Vive en la ruta y no en un estado global porque compartir una carpeta o un
 * perfil de búsqueda entre miembros exige que el enlace diga a qué cuenta
 * pertenece: si no, el receptor lo abre con otra cuenta activa y ve un vacío
 * que parece un error del producto.
 */
@Injectable({ providedIn: 'root' })
export class AccountService {
  private readonly supabase = inject(SupabaseService);
  private readonly auth = inject(AuthService);

  readonly current = signal<Account | null>(null);
  readonly membership = signal<AccountMember | null>(null);

  /** Membresías del usuario. `null` = todavía no se cargaron. */
  readonly memberships = signal<AccountMember[] | null>(null);

  readonly slug = computed(() => this.current()?.slug ?? null);
  readonly accountId = computed(() => this.current()?.id ?? null);
  readonly isOrganization = computed(() => this.current()?.kind === 'organization');

  /** Cuentas a las que puede cambiar: las membresías con su cuenta presente. */
  readonly switchable = computed(() =>
    (this.memberships() ?? []).filter((m): m is AccountMember & { accounts: Account } =>
      Boolean(m.accounts),
    ),
  );

  /** Resuelve `slug` → cuenta + membresía propia. `null` si no existe o no es visible. */
  async resolve(slug: string, force = false): Promise<Account | null> {
    if (!this.supabase.isConfigured) return null;

    const cached = this.current();
    if (!force && cached?.slug === slug && this.membership()) return cached;

    const { data: account } = await this.supabase.client
      .from('accounts')
      .select('id, slug, kind, country, status')
      .eq('slug', slug)
      .maybeSingle();

    if (!account) {
      this.setContext(null, null);
      return null;
    }

    const userId = this.auth.profile()?.id ?? null;
    const { data: member } = userId
      ? await this.supabase.client
          .from('account_members')
          .select('account_id, user_id, role')
          .eq('account_id', (account as Account).id)
          .eq('user_id', userId)
          .maybeSingle()
      : { data: null };

    this.setContext(account as Account, (member as AccountMember | null) ?? null);
    this.remember(slug);
    return account as Account;
  }

  /** Carga las membresías una sola vez por sesión, para el selector de cuenta. */
  async loadMemberships(force = false): Promise<void> {
    if (!force && this.memberships() !== null) return;
    if (!this.supabase.isConfigured) {
      this.memberships.set([]);
      return;
    }

    const userId = this.auth.profile()?.id ?? null;
    if (!userId) {
      this.memberships.set([]);
      return;
    }

    const { data } = await this.supabase.client
      .from('account_members')
      .select('account_id, user_id, role, accounts(id, slug, kind, country, status)')
      .eq('user_id', userId);

    this.memberships.set((data as AccountMember[] | null) ?? []);
  }

  /** Slug preferido: la última cuenta visitada si sigue siendo suya, o la primera. */
  preferredSlug(): string | null {
    const slugs = this.switchable().map((m) => m.accounts.slug);
    if (slugs.length === 0) return null;

    const last = this.readLast();
    return last && slugs.includes(last) ? last : slugs[0];
  }

  /**
   * Cambia de cuenta con recarga completa del documento.
   *
   * No es navegación del router a propósito: el shell tiene servicios de raíz
   * con estado de cuenta —caché de procesos, perfiles seleccionados,
   * preferencias— que una navegación dejaría colados con datos de la cuenta
   * anterior. `living-app` llegó a la misma conclusión por la vía dolorosa.
   */
  switchTo(slug: string): void {
    this.remember(slug);
    if (typeof window === 'undefined') return;
    window.location.assign(`/o/${slug}/app/procesos`);
  }

  /** Ruta con la cuenta activa; cae a la ruta heredada cuando no hay ninguna. */
  link(path: string): string {
    const slug = this.slug();
    return slug ? `/o/${slug}/app${path}` : `/app${path}`;
  }

  /** Solo para pruebas y arranque síncrono. */
  setContext(account: Account | null, member: AccountMember | null): void {
    this.current.set(account);
    this.membership.set(member);
  }

  private readLast(): string | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      return localStorage.getItem(LAST_ACCOUNT_KEY);
    } catch {
      return null;
    }
  }

  private remember(slug: string): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(LAST_ACCOUNT_KEY, slug);
    } catch {
      // Modo privado: recordar la cuenta es una comodidad, no un requisito.
    }
  }
}
